import type { ChatPanelFrame } from '../types';

export const DASHBOARD_ASSISTANT_DOCKED_WIDTH = 400;
export const DASHBOARD_ASSISTANT_DOCKED_BREAKPOINT = 1024;

export const dropdownMotion = {
  initial: { opacity: 0, scale: 0.95, y: 8 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 8 },
  transition: { duration: 0.12, ease: 'easeOut' },
};

export const chatPanelLayoutTransition = {
  type: 'spring',
  stiffness: 420,
  damping: 42,
  mass: 0.9,
};

function getDashboardAssistantBottomBarHeight() {
  if (typeof window === 'undefined') {
    return 52;
  }

  const rawValue = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--dashboard-assistant-bottom-bar-height')
    .trim();
  const parsedValue = Number.parseFloat(rawValue);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 52;
}

export function getMaximizedChatFrame(): ChatPanelFrame {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const isLarge = viewportWidth >= 1024;
  const isMedium = viewportWidth >= 768;
  const top = isMedium ? 16 : 8;
  const left = isLarge ? 272 : isMedium ? 16 : 8;
  const right = isMedium ? 16 : 8;
  const bottom = getDashboardAssistantBottomBarHeight() + (isLarge ? 16 : isMedium ? 12 : 8);

  return {
    height: Math.max(360, viewportHeight - top - bottom),
    left,
    top,
    width: Math.max(320, viewportWidth - left - right),
  };
}

export function getCompactChatFrame(chipElement: HTMLDivElement | null): ChatPanelFrame {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = Math.min(400, viewportWidth - 32);
  const height = Math.min(640, viewportHeight * 0.78);
  const bottom = getDashboardAssistantBottomBarHeight() + 8;
  const chipRect = chipElement?.getBoundingClientRect();
  const desiredLeft = chipRect ? chipRect.right - width : viewportWidth - 16 - width;
  const left = Math.min(Math.max(desiredLeft, 16), viewportWidth - 16 - width);

  return {
    height,
    left,
    top: Math.max(8, viewportHeight - bottom - height),
    width,
  };
}

export function getDockedChatFrame(): ChatPanelFrame {
  const dashboardShell = document.querySelector<HTMLElement>('[data-assistant-shell]');
  const shellRect = dashboardShell?.getBoundingClientRect();
  const headerActionsRect = Array.from(
    document.querySelectorAll<HTMLElement>('[data-dashboard-header-actions]'),
  )
    .map((element) => element.getBoundingClientRect())
    .find(
      (rect) =>
        rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight,
    );
  const top = headerActionsRect?.top ?? shellRect?.top ?? 0;
  // The route cache keeps inactive pages mounted. Page-specific workspace
  // markers can therefore resolve to a hidden, zero-sized route and collapse
  // the dock to its minimum height. The assistant shell is persistent and
  // already ends directly above the bottom bar, so it is the stable boundary
  // for every dashboard route.
  const bottom = shellRect?.bottom ?? window.innerHeight - getDashboardAssistantBottomBarHeight();
  const right = shellRect?.right ?? window.innerWidth;

  return {
    height: Math.max(360, bottom - top),
    left: right - DASHBOARD_ASSISTANT_DOCKED_WIDTH,
    top,
    width: DASHBOARD_ASSISTANT_DOCKED_WIDTH,
  };
}

/** Use the visible viewport, including a software keyboard or browser zoom. */
export function getResponsiveChatFrame(): ChatPanelFrame {
  const viewport = window.visualViewport;
  const availableWidth = viewport?.width ?? window.innerWidth;
  const availableHeight = viewport?.height ?? window.innerHeight;
  const inset = window.innerWidth < 768 ? 0 : Math.min(16, availableHeight / 4, availableWidth / 4);
  const width = Math.max(0, Math.min(window.innerWidth < 768 ? availableWidth : 640, availableWidth - inset * 2));
  const height = Math.max(0, Math.min(window.innerWidth < 768 ? availableHeight : 800, availableHeight - inset * 2));
  return {
    width, height,
    left: (viewport?.offsetLeft ?? 0) + (availableWidth - width) / 2,
    top: (viewport?.offsetTop ?? 0) + (availableHeight - height) / 2,
  };
}
